import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, Shield } from "lucide-react";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    // redirect handled in effect to avoid setState-in-render warning
  }

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Zalogowano. Witaj w panelu Celestia.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Nieprawidłowe dane logowania.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen space-bg flex items-center justify-center px-5 py-10"
      data-testid="admin-login-root"
    >
      <div className="stars-overlay absolute inset-0 pointer-events-none opacity-30" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="industrial-card armor-corners relative w-full max-w-md rounded-md p-8"
      >
        <span className="corner tl" />
        <span className="corner tr" />
        <span className="corner bl" />
        <span className="corner br" />

        <div className="flex items-center gap-2 text-[#B026FF] mb-3">
          <Shield className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.28em] font-semibold">Panel Admina</span>
        </div>
        <h1 className="text-2xl font-black text-white tracking-tight">
          Celestia <span className="text-[#B026FF]">Control</span>
        </h1>
        <p className="text-sm text-[#A69CBB] mt-2">
          Zaloguj się, aby zobaczyć listę zapisów Early Bird.
        </p>

        <div className="mt-7 space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#A69CBB]">
              Email
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#756B8A]" />
              <Input
                id="email"
                data-testid="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#100913]/60 border-white/8 text-white pl-10 h-12 focus-visible:ring-[#B026FF]"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[#A69CBB]">
              Hasło
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#756B8A]" />
              <Input
                id="password"
                data-testid="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#100913]/60 border-white/8 text-white pl-10 h-12 focus-visible:ring-[#B026FF]"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="btn-neon mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-bold tracking-wide uppercase disabled:opacity-60"
        >
          {loading ? "Loguję…" : "Zaloguj się"}
        </button>

        <a
          href="/"
          className="block text-center mt-6 text-xs text-[#756B8A] hover:text-white transition"
          data-testid="admin-back-home"
        >
          ← Wróć na stronę główną
        </a>
      </form>
    </div>
  );
}
